import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { clsx } from 'clsx'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { Image, Post, PostStatus } from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useAddReply,
  useAuthMember,
  usePost,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useCachedMentionsFromPost } from '../common/hooks/useCachedMentionsFromPost.js'
import { useLogin } from '../common/hooks/useLogin.js'
import { getMappingFields } from '../common/utils/post.js'
import { useCanQuitEditor } from '../Composer/utils/state.js'
import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'
import { ReplyEditor, ReplyEditorRef } from '../Reply/ReplyEditor.js'
import { confirmActionOnLockedPost } from './utils.js'

export type ReplyBarRef = {
  scrollIntoView: () => void
  expand: (toggle: boolean) => void
}

type ReplyBarProps = {
  post: Post
  defaultExpand?: boolean
}

export const ReplyBar = forwardRef<ReplyBarRef, ReplyBarProps>(
  ({ post, defaultExpand = false }, ref) => {
    const intl = useI18n()
    const { Link } = useRouter()

    const containerRef = useRef<HTMLDivElement>()
    const [expanded, setExpanded] = useState(defaultExpand)
    const editorRef = useRef<ReplyEditorRef>(null)
    const { data: member } = useAuthMember()
    const { mutate: addReply, isLoading } = useAddReply()
    const { isLoggedIn, showLogin } = useLogin()
    const { generateProfilePath } = useSpaceUtil()
    const canQuitEditor = useCanQuitEditor()

    const picture = member?.profilePicture as Image

    const { data: fullPost, isFetching: fetchingFullPost } = usePost({
      variables: { id: post.id },
      useQueryOptions: {
        enabled: expanded,
      },
    })
    const cachedMentions = useCachedMentionsFromPost(fullPost?.id, {
      enabled: expanded,
    })

    const loading = isLoading || fetchingFullPost

    const availableReplyTypes =
      fullPost?.authMemberProps?.availableReplyTypes ||
      post?.authMemberProps?.availableReplyTypes

    useImperativeHandle(ref, () => ({
      scrollIntoView: () => {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        })
        editorRef.current?.focus()
      },
      expand: setExpanded,
    }))

    const quit = useCallback(() => {
      setExpanded(false)
    }, [])

    const cancel = useCallback(
      isTouched => {
        if (
          canQuitEditor({
            isTouched,
          })
        ) {
          quit()
        }
      },
      [canQuitEditor, quit],
    )

    const onReply = useCallback(
      async (content = '', editor) => {
        const { isEmpty: noText } = editor

        const isEmpty = noText

        if (loading || isEmpty || content === '') {
          return
        }

        const postType = availableReplyTypes?.[0]
        if (!postType?.id) {
          return
        }

        const mappingFields = getMappingFields({
          content,
          postTypeName: postType.name,
        })

        addReply(
          {
            postId: post.id,
            input: {
              postTypeId: postType.id,
              mappingFields,
              publish: true,
            },
          },
          {
            onSuccess: data => {
              if (data.status === PostStatus.BLOCKED) {
                toast({
                  title: intl.$t({
                    id: 'Generics.PendingReview',
                    defaultMessage: 'Pending review',
                  }),
                  description: intl.$t({
                    id: 'Post.Replybar.SuccessToastDescription',
                    defaultMessage:
                      'Your reply will be visible to others once it’s been reviewed by a moderator.',
                  }),
                  status: 'info',
                })
              }

              quit()
            },
            onError: error => {
              const message = error?.response?.errors
                ?.map(e => e.message)
                ?.join('\n')
              toast({
                title: intl.$t({
                  id: 'Post.Replybar.ReplyFailedTitle',
                  defaultMessage: 'Reply failed',
                }),
                description:
                  message ||
                  intl.$t({
                    id: 'Generics.SomethingWentWrongTryAgain',
                    defaultMessage: 'Something went wrong, please try again.',
                  }),
                status: 'error',
              })
            },
          },
        )
      },
      [loading, availableReplyTypes, addReply, post.id, quit, intl],
    )

    const repliedToCount = post.repliedToIds?.length

    // If it is not a reply to a reply, show the avatar below
    const withAvatar = !repliedToCount || repliedToCount > 1

    const containerClasses = clsx(['flex space-s-3 sm:space-s-4 bg-surface'])

    if (fullPost) {
      const [canCreateReply] = hasScopesPermission(post, ['createReply'])
      if ((isLoggedIn && !canCreateReply) || !availableReplyTypes?.length) {
        return null
      }
    }

    if (expanded) {
      return (
        <div className={containerClasses} ref={containerRef}>
          {withAvatar && (
            <>
              <div className="hidden sm:block">
                <Link
                  variant="inherit"
                  href={generateProfilePath()}
                  className="flex w-12 flex-shrink-0"
                >
                  <Avatar
                    name={member?.name}
                    size="lg"
                    src={picture?.urls?.thumb || picture?.url}
                  />
                </Link>
              </div>
              <div className="sm:hidden">
                <Link
                  variant="inherit"
                  href={generateProfilePath()}
                  className="flex w-10 flex-shrink-0"
                >
                  <Avatar
                    name={member?.name}
                    size="md"
                    src={picture?.urls?.thumb || picture?.url}
                  />
                </Link>
              </div>
            </>
          )}
          <div className="flex flex-col bg-surface flex-grow max-w-full min-w-0">
            <div
              className={clsx({
                'text-content-subdued': loading,
              })}
            >
              <ReplyEditor
                onReply={onReply}
                ref={editorRef}
                onCancel={cancel}
                cachedMentions={cachedMentions}
                isLoading={loading}
              />
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className={containerClasses}>
        {withAvatar && (
          <>
            <Link
              variant="inherit"
              href={generateProfilePath()}
              className="flex-shrink-0 w-12 h-12 hover:opacity-90 hidden sm:flex"
            >
              <Avatar
                name={member?.name}
                size="lg"
                src={picture?.urls?.thumb || picture?.url}
              />
            </Link>
            <Link
              variant="inherit"
              href={generateProfilePath()}
              className="flex flex-shrink-0 w-10 h-10 hover:opacity-90 sm:hidden"
            >
              <Avatar
                name={member?.name}
                size="md"
                src={picture?.urls?.thumb || picture?.url}
              />
            </Link>
          </>
        )}
        <button
          type="button"
          onClick={async e => {
            if (!isLoggedIn) {
              e.preventDefault()
              return showLogin()
            }

            if (!(await confirmActionOnLockedPost(intl)(post))) {
              return
            }

            setExpanded(true)
            setTimeout(() => {
              editorRef?.current?.focus()
            }, 300)
          }}
          className="cursor-pointer flex-grow rounded-md px-2.5 mt-0 sm:mt-1 h-10 flex items-center bg-surface-subdued hover:bg-surface-hovered text-content-subdued hover:text-content-hovered"
        >
          <T
            id="Post.ReplyBar.ReplyButtonText"
            defaultMessage="What are your thoughts?"
          />
        </button>
      </div>
    )
  },
)
